import {
    ChatBubbleLeftIcon,
    LinkIcon,
    NewspaperIcon,
    StopCircleIcon,
    UserGroupIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef, Row } from '@tanstack/react-table';
import { isEmpty } from 'radash';
import { useState } from 'react';

import { ReactComponent as RepublishIcon } from '@/assets/icons/publication-republish.svg';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { StatusColumn } from '@/components/shared';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal.tsx';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation.tsx';
import { useUser } from '@/hooks/useUser';
import {
    useReRunPublication,
    useStopPublication,
} from '@/pages/publication/executed.publication.queries';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { formatDateToMedium } from '@/services/utils';
import { Organization } from '@/types/Organization';

import { JobDataType, PublicationDataType } from './executed.publication.types';

const isPublicationAllowed = (
    row: JobDataType | PublicationDataType,
    isVerificationRequired: boolean,
    user: any,
    organization: Organization | undefined
): boolean => {
    if (
        isVerificationRequired &&
        'needsVerification' in row &&
        row.needsVerification &&
        (!organization?.verified)
    ) {
        return false;
    }
    return (
        (user?.roles?.includes('admin')) ||
        isEmpty(row.user) ||
        row.user === (user?.userId ?? null)
    );
};

const usePublicationColumns = (
    refreshExcutedData: () => Promise<void>,
): ColumnDef<PublicationDataType>[] => {
    const $angularJSInjector = getInjector();
    const $timeout = $angularJSInjector.get('$timeout');
    const { t } = useTranslation();
    const { toast } = useToast();
    const { organization } = useOrganization();
    const { user } = useUser();
    const reRunPublication = useReRunPublication();
    const stopPublication = useStopPublication();

    const [pendingRepublishPublicationId, setPendingRepublishPublicationId] = useState<number | null>(null);
    const [pendingStopPublicationId, setPendingStopPublicationId] = useState<number | null>(null);

    const handleRefresh = () => {
        toast.info(t('PUBLICATIONS_REFRESH_MESSAGE'));
        $timeout(() => {
            refreshExcutedData();
        }, 5000);
    };

    const handleRepublish = (id: number) => {
        reRunPublication.mutate(id);
        handleRefresh();
        setPendingRepublishPublicationId(null);
    };

    const handleStop = (id: number) => {
        stopPublication.mutate(id);
        handleRefresh();
        setPendingStopPublicationId(null);
    };

    const getCountTemplate = (
        key: string,
        status?: string
    ): ((props: { row: Row<PublicationDataType> }) => JSX.Element) => {
        // eslint-disable-next-line react/display-name
        return ({ row }) => {
            const value = (row.getValue(key) as number) || 0;
            if (value > 0 && status) {
                return (
                    <a
                        className="tw-text-center"
                        href={`/browse?publicationTaskId=${row.original.id}&publicationTaskStatus=${status}&useTags=true`}
                        target="_self"
                    >
                        {value}
                    </a>
                );
            }
            return <span className="tw-text-center">{value}</span>;
        };
    };

    const getDestinationIcon = (entity: any) => {
        if (entity.destinationType === 'COMMUNICATION_CHANNEL') {
            return ChatBubbleLeftIcon;
        } else if (entity.directPublication) {
            return UserPlusIcon;
        } else {
            return UserGroupIcon;
        }
    };

    return [
        {
            accessorKey: 'totalItemsCount',
            header: t('PUBLICATIONS.ITEMS').toUpperCase(),
            size: 150,
            enableResizing: true,
            cell: ({ row }) => {
                const { itemCounts } = row.original.statusCount || {};
                const success = itemCounts?.SUCCESS || 0;
                const valid = itemCounts?.VALID || 0;
                return <span>{`${success} / ${valid}`}</span>;
            },
        },
        {
            accessorKey: 'taskStatus',
            header: t('PUBLICATIONS.STATUS').toUpperCase(),
            size: 170,
            enableResizing: true,
            cell: ({ row }) => (
                <StatusColumn
                    status={row.original.taskStatus}
                    message={row.original.message}
                />
            ),
        },
        {
            accessorKey: 'destinationName',
            header: t('PUBLICATIONS.DESTINATION').toUpperCase(),
            size: 150,
            enableResizing: true,
            cell: ({ row }) => (
                <div className="tw-flex tw-gap-2 tw-align-middle">
                    <Icon
                        icon={getDestinationIcon(row.original)}
                        size='sm'
                        className="tw-text-gray-300"
                    />
                    <i className={row.original.destinationIcon}></i>
                    <span>{row.original.destinationName}</span>
                </div>
            ),
        },
        {
            id: 'success',
            enableResizing: true,
            accessorKey: 'statusCount.itemCounts.SUCCESS',
            header: t('PUBLICATIONS.ITEMS.SUCCESS').toUpperCase(),
            size: 150,
            cell: getCountTemplate('success', 'SUCCESS'),
        },
        {
            id: 'delivered',
            enableResizing: true,
            accessorKey: 'statusCount.itemCounts.DELIVERED',
            header: t('PUBLICATIONS.ITEMS.DELIVERED').toUpperCase(),
            size: 150,
            cell: getCountTemplate('delivered'),
        },
        {
            id: 'unchanged',
            enableResizing: true,
            accessorKey: 'statusCount.itemCounts.UNCHANGED',
            header: t('PUBLICATIONS.ITEMS.UNCHANGED').toUpperCase(),
            size: 150,
            cell: getCountTemplate('unchanged', 'UNCHANGED'),
        },
        {
            id: 'failed',
            enableResizing: true,
            accessorKey: 'statusCount.itemCounts.ERROR',
            header: t('PUBLICATIONS.ITEMS.FAILED').toUpperCase(),
            size: 150,
            cell: getCountTemplate('failed', 'ERROR'),
        },
        {
            id: 'invalid',
            accessorKey: 'statusCount.itemCounts.INVALID',
            enableResizing: true,
            header: t('PUBLICATIONS.ITEMS.INVALID').toUpperCase(),
            size: 150,
            cell: getCountTemplate('invalid', 'INVALID'),
        },
        {
            id: 'skipped',
            accessorKey: 'statusCount.itemCounts.SKIPPED',
            enableResizing: true,
            header: t('PUBLICATIONS.ITEMS.SKIPPED').toUpperCase(),
            size: 150,
            meta: {
                visible: false,
            },
            cell: getCountTemplate('skipped', 'SKIPPED'),
        },
        {
            accessorKey: 'creationDate',
            header: t('PUBLICATIONS.START').toUpperCase(),
            size: 150,
            enableResizing: true,
            cell: ({ row }) => (
                <div className="tw-whitespace-nowrap">
                    {formatDateToMedium(new Date(row.original.creationDate))}
                </div>
            ),
        },
        {
            accessorKey: 'finishedDate',
            header: t('PUBLICATIONS.END').toUpperCase(),
            size: 150,
            enableResizing: true,
            cell: ({ row }) => {
                const finishedDate = row.original.finishedDate;
                return finishedDate ? (
                    <div className="tw-whitespace-nowrap">
                        {formatDateToMedium(new Date(finishedDate))}
                    </div>
                ) : null;
            },
        },
        {
            accessorKey: 'jobName',
            header: t('ITEM_LIBRARY_PUBLISH.attribute.name').toUpperCase(),
            size: 150,
            enableResizing: true,
        },
        {
            accessorKey: 'user',
            header: t('PUBLICATIONS.USER').toUpperCase(),
            enableResizing: true,
            cell: ({ row }) => {
                const injector = getInjector();
                const $rootScope = getRootScope(injector);
                return <label>{$rootScope.getUserName(row.original.user)}</label>;
            },
        },
        {
            accessorKey: 'publicationType',
            header: t('PUBLICATIONS.PUBLICATION_TYPE').toUpperCase(),
            size: 150,
            enableResizing: true,
            cell: ({ row }) => (
                <span>
                    {t(`PUBLICATIONS.PUBLICATION_TYPE.${row.original.publicationType}`)}
                </span>
            ),
        },
        {
            id: 'actions',
            accessorKey: 'actions',
            header: t('ACTIONS').toUpperCase(),
            meta: {
                pinned: true,
            },
            size: 150,
            enableResizing: false,
            cell: ({ row }) => {
                const { itemCounts } = row.original.statusCount || {};
                return (
                    <div
                        id={`row${row.id}-actions`}
                        className="tw-flex tw-flex-row-reverse tw-gap-1 tw-z-999"
                    >
                        {itemCounts?.SUCCESS && (
                            <a
                                href={`/catalog?publicationTaskId=${row.original.id}&withPublicationTaskChecksums=true`}
                                target="_self"
                                title={t('PUBLICATIONS.BROWSE_IN_ONLINE_CATALOG')}
                            >
                                <IconButton icon={NewspaperIcon} size="md" />
                            </a>
                        )}
                        {itemCounts?.SUCCESS && (
                            <a
                                href={`/browse?publicationTaskId=${row.original.id}`}
                                target="_self"
                                title={t('PUBLICATIONS.BROWSE_IN_ITEMS_LIST')}
                            >
                                <IconButton icon={LinkIcon} size="md" />
                            </a>
                        )}

                        {itemCounts?.SUCCESS &&
                            isPublicationAllowed(
                                row.original,
                                true,
                                user,
                                organization,
                            ) && (
                            <>
                                <IconButton
                                    icon={RepublishIcon}
                                    size="md"
                                    title={t('PUBLICATIONS.REPUBLISH')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPendingRepublishPublicationId(row.original.id);
                                    }}
                                />
                                <ConfirmationModal
                                    title="PUBLICATIONS.CONFIRM_REPUBLISH"
                                    subTitle="PUBLICATIONS.CONFIRM_REPUBLISH_MESSAGE"
                                    discardBtnText="PUBLICATIONS.REPUBLISH"
                                    closeBtnText="CANCEL"
                                    btnVariant="default"
                                    show={pendingRepublishPublicationId === row.original.id}
                                    onSubmit={() => handleRepublish(row.original.id)}
                                    onClose={() => setPendingRepublishPublicationId(null)}
                                />
                            </>
                        )}

                        {!(row.original.finished || row.original.finishing) &&
                            isPublicationAllowed(
                                row.original,
                                false,
                                user,
                                organization,
                            ) && (
                            <>
                                <IconButton
                                    icon={StopCircleIcon}
                                    size="md"
                                    title={t('PUBLICATIONS.STOP')}
                                    disabled={
                                        row.original.taskStatus === 'ABORTED'
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPendingStopPublicationId(row.original.id);
                                    }}
                                />
                                <ConfirmationModal
                                    title="PUBLICATIONS.CONFIRM_STOP"
                                    subTitle="PUBLICATIONS.CONFIRM_STOP_MESSAGE"
                                    discardBtnText="PUBLICATIONS.STOP"
                                    closeBtnText="CANCEL"
                                    btnVariant="destructive"
                                    show={pendingStopPublicationId === row.original.id}
                                    onSubmit={() => handleStop(row.original.id)}
                                    onClose={() => setPendingStopPublicationId(null)}
                                />
                            </>
                        )}
                    </div>
                );
            },
        },
    ];
};

export { isPublicationAllowed, usePublicationColumns };
